import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, Plus, Edit, Trash2, ToggleLeft, ToggleRight,
  Users, Database, CheckCircle, XCircle,
  Package, Star, TrendingUp, Info, Search, Check, X, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import pricingPlanService from '../services/pricingPlanService';
import featureService from '../services/featureService';
import { CreateButton, SaveButton, UpdateButton, DeleteButton, CancelButton } from '../components/CommonButtons';
import Switch from '../components/Switch';
import CommonLoader, { GridLoader } from '../components/CommonLoader';
import { ErrorMessage } from '../utils/validation';
import EnhancedModal from '../components/EnhancedModal';

const PricingPlans = () => {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    storage: '',
    user_limit: '',
    price: '',
    other_details: '',
    is_active: 1,  // Number: 1 for Active, 0 for Inactive
    features: []
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchPlans();
    fetchFeatures();
  }, [selectedStatus]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const params = {
        is_active: selectedStatus  // Add status filter parameter
      };
      const response = await pricingPlanService.getPricingPlans(params);
      
      // Use normalized response structure
      setPlans(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await featureService.getFeatures();
      
      // Use normalized response structure
      setFeatures(response.data || []);
    } catch (error) {
      setFeatures([]);
      // Don't show error toast as features are optional
    }
  };

  const validatePlanForm = () => {
    const errors = {};
    
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Plan name is required';
    }
    
    if (!formData.description || formData.description.trim() === '') {
      errors.description = 'Description is required';
    }
    
    if (!formData.price || formData.price === '') {
      errors.price = 'Price is required';
    } else if (parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be greater than 0';
    }
    
    if (!formData.storage || formData.storage === '') {
      errors.storage = 'Storage is required';
    } else if (parseFloat(formData.storage) <= 0) {
      errors.storage = 'Storage must be greater than 0';
    }
    
    if (!formData.user_limit || formData.user_limit === '') {
      errors.user_limit = 'User limit is required';
    } else if (parseInt(formData.user_limit) <= 0) {
      errors.user_limit = 'User limit must be greater than 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePlan = async () => {
    if (!validatePlanForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const planData = {
        ...formData,
        storage: parseFloat(formData.storage),
        user_limit: parseInt(formData.user_limit),
        price: parseFloat(formData.price)
      };
      await pricingPlanService.createPricingPlan(planData);
      toast.success('Pricing plan created successfully');
      setShowEditModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error('Failed to create pricing plan');
    }
  };

  const handleUpdatePlan = async () => {
    if (!validatePlanForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const planData = {
        ...formData,
        storage: parseFloat(formData.storage),
        user_limit: parseInt(formData.user_limit),
        price: parseFloat(formData.price)
      };
      await pricingPlanService.updatePricingPlan(selectedPlan.id, planData);
      toast.success('Pricing plan updated successfully');
      setShowEditModal(false);
      setSelectedPlan(null);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update pricing plan');
    }
  };

  const handleDeletePlan = async () => {
    try {
      await pricingPlanService.deletePricingPlan(selectedPlan.id);
      toast.success('Pricing plan deleted successfully');
      setShowDeleteModal(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete pricing plan');
    }
  };

  const handleToggleStatus = async (planId) => {
    try {
      await pricingPlanService.togglePlanStatus(planId);
      toast.success('Plan status updated successfully');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update plan status');
    }
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      storage: plan.storage.toString(),
      user_limit: plan.user_limit.toString(),
      price: plan.price.toString(),
      other_details: plan.other_details || '',
      is_active: plan.is_active ? 1 : 0,  // Convert to number: 1 for Active, 0 for Inactive
      features: plan.features || []
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      storage: '',
      user_limit: '',
      price: '',
      other_details: '',
      is_active: 1,  // Default to Active
      features: []
    });
    setFormErrors({});
    setSelectedPlan(null);
  };

  const filteredPlans = plans.filter(plan =>
    plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const formatStorage = (storage) => {
    if (storage >= 1024) {
      return `${(storage / 1024).toFixed(1)} TB`;
    }
    return `${storage} GB`;
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Pricing Plans</h1>
        <p className="text-sm text-gray-600 mt-1">Manage subscription plans and pricing</p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-1 gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <CreateButton
            onClick={() => {
              resetForm();
              setShowEditModal(true);
            }}
            size="sm"
          >
            Add Plan
          </CreateButton>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          [...Array(9)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Header skeleton */}
              <div className="flex items-center justify-between mb-4">
                <div className="bg-gray-200 h-6 w-32 rounded animate-pulse"></div>
                <div className="bg-gray-200 h-8 w-20 rounded-full animate-pulse"></div>
              </div>
              
              {/* Price skeleton */}
              <div className="mb-6">
                <div className="bg-gray-200 h-10 w-24 rounded animate-pulse mb-2"></div>
                <div className="bg-gray-200 h-4 w-40 rounded animate-pulse"></div>
              </div>
              
              {/* Features skeleton */}
              <div className="space-y-3 mb-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center">
                    <div className="bg-gray-200 h-4 w-4 rounded mr-3 animate-pulse"></div>
                    <div className="bg-gray-200 h-4 w-32 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
              
              {/* Button skeleton */}
              <div className="bg-gray-200 h-10 w-full rounded-lg animate-pulse"></div>
            </div>
          ))
        ) : filteredPlans.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No pricing plans found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search' : 'Get started by creating your first pricing plan'}
            </p>
            {!searchTerm && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  resetForm();
                  setShowEditModal(true);
                }}
                className="mt-4 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create First Plan
              </motion.button>
            )}
          </div>
        ) : (
          filteredPlans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-lg shadow-sm border-2 ${
                plan.is_active ? 'border-gray-200' : 'border-gray-100'
              } p-6 hover:shadow-md transition-all relative`}
            >
              {/* Popular Badge */}
              {plan.name.toLowerCase().includes('professional') && (
                <div className="absolute top-0 right-0 bg-primary-500 text-white px-3 py-1 rounded-bl-lg text-xs font-semibold">
                  POPULAR
                </div>
              )}

              <div className="mb-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 pr-2">
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Switch
                      checked={plan.is_active}
                      onChange={() => handleToggleStatus(plan.id)}
                      tooltip={plan.is_active ? 'Click to deactivate' : 'Click to activate'}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold text-gray-900">
                    {formatPrice(plan.price)}
                  </span>
                  <span className="ml-2 text-gray-500">/month</span>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm">
                  <Database className="w-4 h-4 text-gray-400 mr-3" />
                  <span className="text-gray-700">
                    <strong>{formatStorage(plan.storage)}</strong> Storage
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <Users className="w-4 h-4 text-gray-400 mr-3" />
                  <span className="text-gray-700">
                    <strong>{plan.user_limit}</strong> Users
                  </span>
                </div>
                {plan.features && plan.features.length > 0 && (
                  <div className="flex items-start text-sm">
                    <Star className="w-4 h-4 text-gray-400 mr-3 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-gray-700">
                        <strong>{plan.features.length}</strong> Premium Features
                      </span>
                      {/* Show feature names if available */}
                      {features.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {plan.features.slice(0, 3).map(featureId => {
                            const feature = features.find(f => f.id === featureId);
                            return feature ? (
                              <span
                                key={featureId}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                              >
                                {feature.name}
                              </span>
                            ) : null;
                          })}
                          {plan.features.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
                              +{plan.features.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {plan.other_details && (
                <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start text-sm">
                    <Info className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                    <p className="text-gray-600">{plan.other_details}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(plan)}
                  className="flex-1 px-3 py-1.5 text-sm border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center gap-1 focus:border-transparent"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowDeleteModal(true);
                  }}
                  className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors focus:border-transparent"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

            </motion.div>
          ))
        )}
      </div>

      {/* Edit/Create Modal */}
      <EnhancedModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        title={selectedPlan ? 'Edit Pricing Plan' : 'Create Pricing Plan'}
        size="xlarge"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (formErrors.name) {
                        setFormErrors({ ...formErrors, name: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="e.g., Professional Plan"
                  />
                  {formErrors.name && <ErrorMessage message={formErrors.name} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({ ...formData, price: e.target.value });
                      if (formErrors.price) {
                        setFormErrors({ ...formErrors, price: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border ${formErrors.price ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="e.g., 999.99"
                  />
                  {formErrors.price && <ErrorMessage message={formErrors.price} />}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    if (formErrors.description) {
                      setFormErrors({ ...formErrors, description: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border ${formErrors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="Brief description of the plan"
                />
                {formErrors.description && <ErrorMessage message={formErrors.description} />}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Storage (GB) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.storage}
                    onChange={(e) => {
                      setFormData({ ...formData, storage: e.target.value });
                      if (formErrors.storage) {
                        setFormErrors({ ...formErrors, storage: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border ${formErrors.storage ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="e.g., 100"
                  />
                  {formErrors.storage && <ErrorMessage message={formErrors.storage} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User Limit <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.user_limit}
                    onChange={(e) => {
                      setFormData({ ...formData, user_limit: e.target.value });
                      if (formErrors.user_limit) {
                        setFormErrors({ ...formErrors, user_limit: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border ${formErrors.user_limit ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="e.g., 10"
                  />
                  {formErrors.user_limit && <ErrorMessage message={formErrors.user_limit} />}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Details
                </label>
                <textarea
                  value={formData.other_details}
                  onChange={(e) => setFormData({ ...formData, other_details: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 h-24"
                  placeholder="Any additional features or details..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <Switch
                  checked={formData.is_active === 1}
                  onChange={(value) => setFormData({ ...formData, is_active: value ? 1 : 0 })}
                  label={formData.is_active === 1 ? 'Active Plan' : 'Inactive Plan'}
                />
              </div>

              {/* Features Selection */}
              {features.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {features.map((feature) => (
                        <label
                          key={feature.id}
                          className="flex items-start cursor-pointer hover:bg-gray-50 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            value={feature.id}
                            checked={formData.features.includes(feature.id)}
                            onChange={(e) => {
                              const featureId = parseInt(e.target.value);
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  features: [...formData.features, featureId]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  features: formData.features.filter(id => id !== featureId)
                                });
                              }
                            }}
                            className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="ml-3 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {feature.name}
                            </div>
                            {feature.description && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {feature.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Select the features included in this plan
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <CancelButton
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              />
              {selectedPlan ? (
                <UpdateButton
                  onClick={handleUpdatePlan}
                >
                  Update
                </UpdateButton>
              ) : (
                <SaveButton
                  onClick={handleCreatePlan}
                >
                  Create
                </SaveButton>
              )}
            </div>
        </div>
      </EnhancedModal>

      {/* Delete Modal */}
      <EnhancedModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPlan(null);
        }}
        title="Delete Pricing Plan"
        size="small"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete <strong>{selectedPlan?.name}</strong>? 
              This action cannot be undone and may affect existing subscriptions.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedPlan(null);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePlan}
                className="bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors px-3 py-1.5 text-sm"
              >
                Delete
              </button>
            </div>
        </div>
      </EnhancedModal>
    </div>
  );
};

export default PricingPlans;